import React, { useEffect, useState } from 'react';
import { ArrowLeft, ShieldCheck, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEOHead from '../components/SEOHead';
import { api } from '../services/api';

// Custom renderers com estilos Tailwind explícitos — evitamos depender de
// @tailwindcss/typography (que não está instalado no projeto).
function H2({ children, ...p }) { return <h2 className="text-xl font-bold mt-8 mb-4" {...p}>{children}</h2>; }
function H3({ children, ...p }) { return <h3 className="text-base font-semibold text-[var(--primary)] mt-6 mb-2" {...p}>{children}</h3>; }
function Para({ children, ...p }) { return <p className="text-gray-600 leading-relaxed mb-4" {...p}>{children}</p>; }
function UL({ children, ...p }) { return <ul className="text-gray-600 space-y-2 list-disc pl-6 mb-4" {...p}>{children}</ul>; }
function LI({ children, ...p }) { return <li className="leading-relaxed" {...p}>{children}</li>; }
function Strong({ children, ...p }) { return <strong className="font-semibold text-gray-800" {...p}>{children}</strong>; }

// Custom link renderer para os termos: internal (/...) navega via SPA;
// externo abre em nova aba. Extraído para fora do componente para não
// recriar a cada render (react/no-unstable-nested-components).
function TermosLink({ navigate, href, children, ...rest }) {
  const isInternal = href && href.startsWith('/');
  if (isInternal) {
    return (
      <button
        type="button"
        onClick={() => navigate(href)}
        className="text-[var(--primary)] hover:underline font-medium"
        {...rest}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[var(--primary)] hover:underline"
      {...rest}
    >
      {children}
    </a>
  );
}

export default function TermosDeUso() {
  const navigate = useNavigate();
  const lastUpdated = '7 de Julho de 2026';
  const termsVersion = 'v1.2';

  // Conteúdo dos termos é o MESMO que o admin edita em
  // /admin/conteudo/termos-de-uso. Puxamos via /v2/admin/paginas/public/termos
  // (interceptor injeta municipio_slug); backend faz fallback:
  // (1) versão custom do município, (2) versão global, (3) markdown default.
  const [conteudo, setConteudo] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/v2/admin/paginas/public/termos')
      .then((r) => { if (alive) setConteudo(r.data?.conteudo || ''); })
      .catch(() => { if (alive) setConteudo(''); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const mdComponents = React.useMemo(() => ({
    h1: H2, // markdown # → h2 (o h1 já está no header da página)
    h2: H2,
    h3: H3,
    p: Para,
    ul: UL,
    ol: UL,
    li: LI,
    strong: Strong,
    // eslint-disable-next-line react/no-unstable-nested-components
    a: (props) => <TermosLink navigate={navigate} {...props} />,
  }), [navigate]);

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title="Termos de Uso"
        description="Leia os termos e condições de uso da plataforma Empreende Comunidade."
        canonical="/termos-de-uso"
      />
      {/* Header */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[var(--primary)] hover:underline mb-6"
            data-testid="termos-voltar-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope' }}>
            Termos de Uso
          </h1>
          <p className="text-gray-500 text-sm">
            Última atualização: {lastUpdated} · Versão {termsVersion}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* LGPD Badge */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="text-sm">
            <p className="font-semibold text-emerald-800">Aceitação eletrônica registrada</p>
            <p className="text-emerald-700">
              Ao se cadastrar, sua aceitação destes Termos e da Política de Privacidade é
              registrada com data, hora e impressão digital (versão dos documentos + hash do IP),
              em conformidade com o Art. 8º §1º da LGPD.
            </p>
          </div>
        </div>

        <div
          className="bg-white rounded-2xl shadow-sm p-8 prose prose-gray max-w-none break-words [overflow-wrap:anywhere]"
          data-testid="termos-conteudo"
        >
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 py-8">
              <Loader2 className="w-4 h-4 animate-spin" /> Carregando termos…
            </div>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {conteudo}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
}
